import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import PropTypes from "prop-types";

import Button from "@cayuse/shared-react.components.button";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import Certification from "components/Certification";
import CompleteReview from "components/CompleteReview";
import FormSummary from "components/FormSummary";
import StatusDropdown from "components/FormSummary/StatusDropdown";
import SPLink from "components/SPLink";
import StatusCommentModal from "components/StatusCommentModal";
import {
	API_AWARD_FEED,
	AWARD_REVIEW_STATUS,
	COLUMN_HEADER_KEYS,
	DEFAULT_AWARD_COLUMN_HEADERS,
	IS_DEV,
	MODIFICATION_STATUS,
	PROJECTS_BASE_ROUTE,
	PROPOSALS_BASE_ROUTE
} from "globals/constants";
import { useColumnHeaders,
		 useDictionary,
		 useResolutions,
		 useFormAccesses,
		 useCertificationText
} from "hooks";
import store from "store/store";
import { getAward, getAwardValues } from "store/actions/award";
import { awardSelectors, tenantSelectors, taskListSelectors, formSelectors, modificationListSelectors } from "store/selectors";
import { completeCertificationAwardTask } from "store/actions/tasks/completeCertificationTask";
import {
	filterAwardReviewTasks,
	filterAwardCertificationTasks,
	notifyError,
} from "utils";

import styles from "./styles.css";
import { api } from "services";


const DEFAULT_ACTIVE_COLUMN_HEADERS = [
	DEFAULT_AWARD_COLUMN_HEADERS.PI,
	DEFAULT_AWARD_COLUMN_HEADERS.SPONSOR,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_START_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.ADMIN_UNIT,
	DEFAULT_AWARD_COLUMN_HEADERS.PRIME_SPONSOR,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_END_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.ADMIN_ASSIGNED,
	DEFAULT_AWARD_COLUMN_HEADERS.OBLIGATED_AMOUNT,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_NOTICE_RECEIVED,
	{ isPlaceholder: true },
	DEFAULT_AWARD_COLUMN_HEADERS.TOTAL_EXPECTED_AMOUNT,
	DEFAULT_AWARD_COLUMN_HEADERS.INSTRUMENT_TYPE
];

const AwardSummary = ({ isFormComplete }) => {
	const { awardId } = useParams();
	const award = useSelector(awardSelectors.award);
	const { savingIndicator: isFormSaving, isEditMode, isFileUploading } = useSelector(formSelectors.entity);
	const { isFetching: isAwardValuesFetching, data } = useSelector(awardSelectors.values);
	const { items: awardTasks } = useSelector(taskListSelectors.awardTasks);
	const { awardStatuses } = useSelector(tenantSelectors.tenant);
	const { items: modificationList } = useSelector(modificationListSelectors.content);

	const formInstance = useMemo(() => {
		return {
			instance: award,
			instanceId: award.awardId,
			values: data,
		};
	}, [ award, data ]);

	const [ resolutionSelected, setResolutionSelected ] = useState(null);
	const [ resolutionsVisible, setResolutionsVisible ] = useState(false);

	const [ statusOverrides, setStatusOverrides ] = useState([]);
	const [ statusOverrideId, setStatusOverrideId ] = useState(null);
	const [ isStatusLoading, setIsStatusLoading ] = useState(false);

	const [ commentModalVisible, setCommentModalVisible ] = useState(false);
	const [ commentModalTitle, setCommentModalTitle ] = useState("");
	const [ comment, setComment ] = useState("");

	const [ statusSelected, setStatusSelected ] = useState(null);

	const [ isLoading, setIsLoading ] = useState(false);

	const { defaultColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.AWARD, {
		defaultColumnHeaders: DEFAULT_ACTIVE_COLUMN_HEADERS
	});

	const dictionary = useDictionary("awards.awardDetails.summary");

	const resolutions = useResolutions(COLUMN_HEADER_KEYS.AWARD);

	const certificationTextData = useCertificationText(COLUMN_HEADER_KEYS.AWARD);

	const formAccesses = useFormAccesses("award", award.awardId);

	const hasDraft = useMemo(() => modificationList.some(item =>
		item.modificationStatus === MODIFICATION_STATUS.draft
	), [ modificationList ]);

	const isCertifiable = award.statusName === AWARD_REVIEW_STATUS.inReview;
	const isUnderReview = award.statusName === AWARD_REVIEW_STATUS.inReview;

	const decisionOptions = [
		{
			key: "approved",
			value: "approved",
			text: dictionary.completeReview.decisions?.approved || "Approve"
		},
		{
			key: "defer",
			value: "deferred",
			text: dictionary.completeReview.decisions?.defer || "Defer"
		}
	];

	const routeForReview = async () => {
		setIsLoading(true);

		try {
			const id = award?.awardId;
			if (!id) {
				throw new Error("Award ID is missed. Award cannot be routed for review.");
			}
			await api.awards.routeForReview({ id: id });
			await getAward(id, { isRefresh: true });

		} catch (error) {
			console.error(error);
			const message = IS_DEV ? error.message : store.getState().dictionary.proposals.routing.errors.routeReviewFailed;
			notifyError(message, error);
		} finally {
			setIsLoading(false);
		}
	};

	const isCorrectAwardStatus = statusSelected === AWARD_REVIEW_STATUS.inReview ||
		statusSelected === AWARD_REVIEW_STATUS.setup;
	const hasCorrectReviewStatus = ((award.reviewStatus == null && !award.reviewProcessId) ||
		award.reviewStatus === AWARD_REVIEW_STATUS.notStarted || award.reviewStatus === AWARD_REVIEW_STATUS.deferred);
	const routeButtonDisabled = !isCorrectAwardStatus ||
		!hasCorrectReviewStatus ||
		!isFormComplete ||
		!formAccesses.changeStatus ||
		isLoading ||
		!award.formTaskId;

	const actionButtons = [
		<CompleteReview key="CompleteReview"
			formInstance={award}
			instanceId={award.awardId}
			instanceTasks={awardTasks}
			instanceFeedEndpoint = {API_AWARD_FEED}
			dictionary={dictionary.completeReview}
			getInstance={getAward}
			filterInstanceReviewTasks={filterAwardReviewTasks}
			decisionOptions={decisionOptions}
			isCompleteReviewActive={isUnderReview}
		/>,
		<Certification  key="Certification"
			formInstance={award}
			instanceId={award.awardId}
			instanceTasks={awardTasks}
			getInstance={getAward}
			dictionary={dictionary.certification}
			isCertifiable={isCertifiable}
			certificationTextData ={certificationTextData}
			completeCertificationTask={completeCertificationAwardTask}
			filterInstanceCertificationTasks={filterAwardCertificationTasks}
		/>,
		<Button key="RouteForReview"
			disabled={routeButtonDisabled || isFormSaving || isFileUploading}
			visible={`${formAccesses.changeStatus || ""}`}
			busy={isLoading}
			loadingMessage="Save in process"
			onClick={routeForReview}
		>
			{dictionary.routeForReview || "Route for Review"}
		</Button>
	];

	const onCommentOpen = (fromStatus, toStatus, toStatusId) => {
		const isResolutionVisible = (toStatus === "Closed");

		setCommentModalTitle(`Change Award Status: ${fromStatus} to ${toStatus}`);
		setStatusOverrideId(toStatusId);
		setComment("");
		setResolutionSelected(null);
		setResolutionsVisible(isResolutionVisible);
		setCommentModalVisible(true);
		setStatusSelected(toStatus);
	};

	const onCommentClose = (actionType = "close") => {
		setCommentModalVisible(false);
		actionType !== "confirm" && setStatusSelected(award.statusName);
	};

	const onConfirm = async () => {
		setIsStatusLoading(true);

		try {
			await api.awards.updateStatus(award.awardId, {
				id: statusOverrideId,
				comment: comment,
				resolution: resolutionSelected
			});

			onCommentClose("confirm");
			getAward(award.awardId);

		} catch (error) {
			const message = IS_DEV ? error.message : dictionary.completeReview.errors.reviewNotCompleted;
			notifyError(message, error);

		} finally {
			setIsStatusLoading(false);
		}
	};

	useEffect(() => {
		if (!award.awardId) {
			getAward(awardId);
		}
	}, [ award ]);

	useEffect(() => {
		if (isNonEmptyString(award.awardId) && !isAwardValuesFetching) {
			getAwardValues({ awardId: award.awardId });
		}
	}, [ award.awardId ]);

	const loadAwardStatus = useCallback(async awardId => {
		try {
			const data = await api.awards.getStatus(awardId);
			setStatusOverrides(data);

		} catch (error) {
			notifyError("Something went wrong.", error);
			setStatusOverrides([]);
		}
	}, []);

	useEffect(() => {
		if (isNonEmptyString(award.awardId)) {
			loadAwardStatus(award.awardId);
		}
	}, [ award.awardId, award.modificationStatus, formAccesses.changeStatus, loadAwardStatus ]);

	useEffect(() => {
		if (award && isNonEmptyString(award.statusName) && award.statusName !== statusSelected) {
			setStatusSelected(award.statusName);
			loadAwardStatus(award.awardId);
		}
	}, [ award, statusSelected ]);

	const statusOptions = useMemo(() => {
		if (isNonEmptyArray(awardStatuses)) {
			return awardStatuses.map(item => {
				const disabled = !statusOverrides.some(override => item.description === override.description);
				const toStatus = item.description;
				const toStatusId = item.id;

				return {
					className: styles.statusOverride,
					key: item.id,
					value: item.description,
					text: item.description,
					disabled: disabled,
					onClick: () => !disabled && onCommentOpen(statusSelected, toStatus, toStatusId)
				};
			});
		}
		return [];
	}, [ award, awardStatuses, statusOverrides, statusSelected ]);

	const resolutionOptions = useMemo(() => {
		if (!resolutions) {
			return [];
		}
		return resolutions
			.filter(resolution => resolution.active)
			.sort((a, b) => (a.value > b.value) ? 1 : -1)
			.map(item => {
				return {
					key: item.value,
					value: item.value,
					label: item.value
				};
			});
	}, [ resolutions ]);

	const linkSection = useMemo( () => {
		const items = [];

		const link = ({ label, url, text }) => {
			return label === "Budget" ?
				<>
					<span className={styles.label}>{label}:</span>
					<a href={url} aria-label={`Go to ${label}: ${text}`}>
						{text}
					</a>
				</> :
				<>
					<span className={styles.label}>{label}:</span>
					<SPLink to={url} aria-label={`Go to ${label}: ${text}`}>
						{text}
					</SPLink>
				</>;
		};

		if(award) {
			const { parentFormType, parentId, parentNumber, projectId, projectNumber } = award;

			items.push(
				link({ label: "Project", url: `${PROJECTS_BASE_ROUTE}/${projectId}`, text: projectNumber }),
				parentFormType === COLUMN_HEADER_KEYS.PROPOSAL ? link({ label: "Proposal", url: `${PROPOSALS_BASE_ROUTE}/${parentId}`, text: parentNumber }) : null
			);
		}

		if(isNonEmptyArray(items)) {
			return (
				<ul className={styles.summaryLinkList}>
					{items.map((item, index) => item ? <li key={`summary-link-${index}`} className={styles.summaryRow}>{item}</li> : "")}
				</ul>
			);
		}
	}, [ award ]);

	return (
		<div data-component="awardSummary">
			<StatusCommentModal
				comment={comment}
				title={commentModalTitle}
				resolutions={resolutionOptions}
				resolutionSelected={resolutionSelected}
				resolutionsVisible={resolutionsVisible}
				visible={commentModalVisible}
				onClose={onCommentClose}
				onConfirm={onConfirm}
				onCommentChange={value => setComment(value)}
				onResolutionChange={value => setResolutionSelected(value)}
				isLoading={isStatusLoading}
				formType={COLUMN_HEADER_KEYS.AWARD}
			/>
			<FormSummary
				summaryTitle={ dictionary.title || "Award Summary" }
				actionButtons={ actionButtons }
				statusDropdown={
					<StatusDropdown
						options={statusOptions}
						value={statusSelected}
						disabled={
							!formAccesses.changeStatus ||
							isFormSaving ||
							isEditMode ||
							isFileUploading ||
							isLoading ||
							hasDraft
						}
						ariaLabel="Select award status"
						label="Award status:"
					/>
				}
				defaultColumnHeaders={ defaultColumnHeaders }
				formInstance={ formInstance }
				formType={COLUMN_HEADER_KEYS.AWARD}
				thirdColumnContent={linkSection}
			/>
		</div>
	);
};

AwardSummary.propTypes = { // todo: flesh this out
	award: PropTypes.shape({
		isFormComplete: PropTypes.bool,
		status: PropTypes.string
	})
};

export default AwardSummary;
